import * as React from 'react';
import { getXValue } from '@react-financial-charts/core/lib/utils/ChartDataUtil';
import { saveNodeType } from 'react-financial-charts';
import { HoverTextNearMouse } from '@react-financial-charts/interactive/lib/components/index';
import { InteractiveText } from './index';
import { isDefined } from 'react-financial-charts';
import { isPoint } from '@/utils/useShapeActions';
import type { DrawingSharedState, HoverTextConfig, MoreProps } from '@/types/commontypes';

export interface EachTextProps {
  readonly index?: number;
  readonly uuid: string;
  readonly position: [number, number];
  readonly bgFill: string;
  readonly bgStrokeWidth: number;
  readonly bgStroke?: string;
  readonly textFill: string;
  readonly fontWeight: string;
  readonly fontFamily: string;
  readonly fontStyle: string;
  readonly fontSize: number;
  readonly text: string;
  readonly selected: boolean;
  readonly showBackground: boolean;
  readonly onDrag?: (e: React.MouseEvent, uuid: string, xyValue: [number, number]) => void;
  readonly onDragComplete?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onSelect?: (e: React.MouseEvent, uuid: string, moreProps: MoreProps) => void;
  readonly hoverText: HoverTextConfig;
  readonly onDragStart?: (uuid: string) => void;
  readonly onHover?: (uuid: string | null, moreProps?: MoreProps) => void;
  readonly locked: boolean;
  readonly onDoubleClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly sharedState: DrawingSharedState;
  readonly isAnyShapeEnabled?: boolean;
}

interface EachTextState {
  hover: boolean;
  dragging: boolean;
}

export class EachText extends React.Component<EachTextProps, EachTextState> {
  public static defaultProps = {
    bgFill: 'rgba(120, 123, 134, 0.2)',
    bgOpacity: 1,
    textFill: 'rgba(29, 85, 215,1)',
    fontFamily: 'Arial',
    bgStrokeWidth: 1,
    selected: false,
    hoverText: {
      ...HoverTextNearMouse.defaultProps,
      enable: true,
      bgHeight: 'auto',
      bgWidth: 'auto',
      text: 'Click to select object',
    },
    showBackground: true,
    locked: false,
  };

  private dragStartPosition: { position: unknown; dx: number; dy: number } | undefined;

  private saveNodeType: <T>(type: string) => React.Ref<T>;
  private lineInteractive: string;

  public constructor(props: EachTextProps) {
    super(props);

    this.handleHover = this.handleHover.bind(this);

    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDrag = this.handleDrag.bind(this);

    this.saveNodeType = saveNodeType.bind(this);
    this.lineInteractive = 'react-financial-charts-pointer-cursor';
    this.state = {
      hover: false,
      dragging: false,
    };
  }

  public render() {
    const {
      position,
      bgFill,
      bgStroke,
      bgStrokeWidth,
      textFill,
      fontFamily,
      fontSize,
      fontWeight,
      fontStyle,
      text,
      hoverText,
      selected,
      onDoubleClick,
      showBackground,
    } = this.props;
    const { hover } = this.state;

    const hoverHandler = {
      onHover: this.handleHover,
      onUnHover: this.handleHover,
    };

    if (!isPoint(position)) return null;
    const { dragging } = this.state;
    if (dragging) {
      this.lineInteractive = 'react-financial-charts-grabbing-cursor';
    } else if (!dragging) {
      this.lineInteractive = 'react-financial-charts-pointer-cursor';
    } else {
      this.lineInteractive = 'react-financial-charts-pointer-cursor';
    }
    const {
      enable: hoverTextEnabled,
      selectedText: hoverTextSelected,
      text: hoverTextUnselected,
      ...restHoverTextProps
    } = hoverText;
    return (
      <g>
        <InteractiveText
          ref={this.saveNodeType('text')}
          selected={selected || hover}
          interactiveCursorClass={this.lineInteractive}
          {...hoverHandler}
          onDragStart={this.handleDragStart}
          onDrag={this.handleDrag}
          onDragComplete={this.handleDragComplete}
          position={position}
          bgFillStyle={bgFill}
          bgStroke={bgStroke || textFill}
          bgStrokeWidth={bgStrokeWidth}
          textFill={textFill}
          fontFamily={fontFamily}
          fontStyle={fontStyle}
          fontWeight={fontWeight}
          fontSize={fontSize}
          text={text}
          showBackground={showBackground}
          onMouseDown={this.handleShapeMouseDown}
          onDoubleClick={onDoubleClick}
          paddingX={10}
          paddingY={6}
        />
        <HoverTextNearMouse
          show={hoverTextEnabled && hover}
          {...restHoverTextProps}
          text={selected ? hoverTextSelected : hoverTextUnselected}
        />
      </g>
    );
  }

  private readonly handleDragComplete = (e: React.MouseEvent, moreProps: MoreProps) => {
    this.setState({
      dragging: false,
    });

    const { onDragComplete, locked } = this.props;
    if (onDragComplete === undefined || locked) {
      return;
    }

    onDragComplete(e, moreProps);
  };

  private readonly handleShapeMouseDown = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { onSelect, uuid, sharedState } = this.props;
    if (sharedState?.status === 'started') return;

    if (onSelect && isDefined(uuid)) {
      onSelect(e, uuid, moreProps);
    }
  };

  private readonly handleHover = (_: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onHover, sharedState, isAnyShapeEnabled } = this.props;
    if (sharedState?.status === 'started' || isAnyShapeEnabled) return;
    const hovering = moreProps.hovering || false;
    if (this.state.hover !== hovering) {
      this.setState({
        hover: hovering,
      });
      if (hovering && onHover) {
        onHover(uuid ?? null, moreProps);
      }
    }
  };

  private readonly handleDrag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, locked } = this.props;

    if (onDrag === undefined || locked) {
      return;
    }

    const { mouseXY, xAccessor, xScale, chartConfig } = moreProps;
    const [, mouseY] = mouseXY;
    const { yScale } = chartConfig;
    const { plotData } = moreProps;
    const hovering = moreProps.hovering || false;

    const { dx, dy } = this.dragStartPosition!;
    const xValue = xScale.invert(xScale(getXValue(xScale, xAccessor, mouseXY, plotData ?? [])) - dx);
    // xScale.invert(xScale(xAccessor(currentItem)) - dx);
    const xyValue: [number, number] = [xValue, yScale.invert(mouseY - dy)];
    this.setState({ dragging: true });
    if (hovering) {
      onDrag(e, uuid, xyValue);
    }
  };

  private readonly handleDragStart = (_: React.MouseEvent, moreProps: MoreProps) => {
    const { position, onDragStart, uuid, locked } = this.props;
    const { mouseXY, xScale, chartConfig } = moreProps;
    const { yScale } = chartConfig;
    if (locked) return;
    const [mouseX, mouseY] = mouseXY;

    const [textCX, textCY] = position;
    const dx = mouseX - xScale(textCX);
    const dy = mouseY - yScale(textCY);

    if (onDragStart && uuid !== undefined) {
      onDragStart(uuid);
    }

    this.setState({ dragging: true });

    this.dragStartPosition = {
      position,
      dx,
      dy,
    };
  };
}
